import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { BrandSection } from "@/components/brand/BrandSection";
import { LogoCard } from "@/components/brand/LogoCard";
import { ColourSwatch } from "@/components/brand/ColourSwatch";
import { FontCard } from "@/components/brand/FontCard";
import { ImageCard } from "@/components/brand/ImageCard";
import { CompanyDetails, LogoRules } from "@/components/brand/CompanyDetails";
import { appsApi, notifyError } from "@/lib/appsApi";
import { useNoIndex } from "@/lib/useNoIndex";
import { LOGOS, COLOURS, FONTS, IMAGES } from "@/lib/brand";
import { downloadBrandKit } from "@/lib/brandZip";

const DownloadAll = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await downloadBrandKit();
      toast.success("Brand kit downloaded");
    } catch (err) {
      notifyError(`Building the brand kit zip failed: ${err?.message || "unknown error"}. Try again, or download the files one at a time below.`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:opacity-60" data-testid="brand-download-all">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download everything (.zip)
    </button>
  );
};

export default function BrandKit() {
  useNoIndex();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    document.title = "Brand kit | Aero Soft Solutions";
    appsApi.get("/auth/me").then(() => setReady(true)).catch(() => navigate("/apps"));
  }, [navigate]);

  if (!ready) return <div className="sky-wash flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="sky-wash min-h-screen" data-testid="brand-kit-page">
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <Logo />
          <button onClick={() => navigate("/apps")} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink hover:border-sky-600" data-testid="brand-back-button"><ArrowLeft className="h-4 w-4" />Back</button>
        </div>
        <p className="mt-12 font-mono text-xs uppercase tracking-[0.25em] text-sky-700">For print &amp; design suppliers</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-4xl font-semibold tracking-[-0.03em] text-ink sm:text-5xl lg:text-6xl">Brand <span className="font-serif font-normal italic text-sky-600">kit</span></h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">Logos, colours, fonts, images and company details for Aero Soft Solutions Pty Ltd. The zip includes every file on this page plus a plain-text brand guide.</p>
          </div>
          <DownloadAll />
        </div>

        <BrandSection id="logos" eyebrow="01 · Logos" title="Logo files" intro="SVG files are vector with the lettering converted to outlines, so they scale to any size and need no fonts installed. PNGs are high-resolution with transparent backgrounds unless stated.">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{LOGOS.map((l) => <LogoCard key={l.id} logo={l} />)}</div>
          <div className="mt-6"><LogoRules /></div>
        </BrandSection>

        <BrandSection id="colours" eyebrow="02 · Colours" title="Colour palette" intro="Match to the HEX or RGB values. *CMYK figures are a straight conversion from RGB, so please proof them on your press.">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">{COLOURS.map((c) => <ColourSwatch key={c.hex} colour={c} />)}</div>
        </BrandSection>

        <BrandSection id="fonts" eyebrow="03 · Typography" title="Fonts" intro="All four typefaces are free under the SIL Open Font License, so suppliers can install and use them. Licence files are included in the zip.">
          <div className="grid gap-5 lg:grid-cols-2">{FONTS.map((f) => <FontCard key={f.name} font={f} />)}</div>
        </BrandSection>

        <BrandSection id="images" eyebrow="04 · Imagery" title="Corporate images" intro="The photographs used on the Aero Soft Solutions website. These are web-resolution files, so the print size at 300 dpi is shown on each one.">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{IMAGES.map((i) => <ImageCard key={i.file} img={i} />)}</div>
        </BrandSection>

        <BrandSection id="company" eyebrow="05 · Details" title="Company details" intro="For letterheads, invoices, business cards and signage.">
          <CompanyDetails />
        </BrandSection>
      </div>
    </div>
  );
}
